import { AnimatePresence, motion } from "motion/react"
import type React from "react"
import { createPortal } from "react-dom"

import { DialogBox } from "@/components/DialogBox"
import { useTypewriter } from "@/hooks/useTypewriter"
import { i18n } from "@/i18n"
import { useEventStore } from "@/store"

import styles from "./index.module.scss"

export const DialogOverlay: React.FC = () => {
    const { currentEvent, phase, dialogueIndex, advanceDialogue } =
        useEventStore()

    const currentDialogue = currentEvent?.dialogues[dialogueIndex] ?? null

    const dialogueText = currentDialogue
        ? i18n.get(currentDialogue.text as never)
        : ""

    const { displayed, isDone, skip } = useTypewriter(dialogueText, 30)

    const handleClick = () => {
        if (!isDone) {
            skip()
            return
        }
        advanceDialogue()
    }

    const curSide: "left" | "right" = currentDialogue?.side ?? "left"

    let leftSlot: { character: string; expression?: string } | undefined
    let rightSlot: { character: string; expression?: string } | undefined

    if (currentDialogue && currentEvent) {
        const dialogues = currentEvent.dialogues
        if (curSide === "left") {
            leftSlot = {
                character: currentDialogue.speaker,
                expression: currentDialogue.expression,
            }
            for (let i = dialogueIndex - 1; i >= 0; i--) {
                if ((dialogues[i].side ?? "left") === "right") {
                    rightSlot = {
                        character: dialogues[i].speaker,
                        expression: dialogues[i].expression,
                    }
                    break
                }
            }
        } else {
            rightSlot = {
                character: currentDialogue.speaker,
                expression: currentDialogue.expression,
            }
            for (let i = dialogueIndex - 1; i >= 0; i--) {
                if ((dialogues[i].side ?? "left") === "left") {
                    leftSlot = {
                        character: dialogues[i].speaker,
                        expression: dialogues[i].expression,
                    }
                    break
                }
            }
        }
    }

    return createPortal(
        <AnimatePresence>
            {phase === "dialogue" && currentDialogue && (
                <>
                    <motion.div
                        className={styles.overlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        onClick={handleClick}
                    />
                    <motion.div
                        className={styles.dialogPosition}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        onClick={handleClick}
                    >
                        <DialogBox
                            leftCharacter={leftSlot?.character}
                            leftExpression={leftSlot?.expression}
                            rightCharacter={rightSlot?.character}
                            rightExpression={rightSlot?.expression}
                            active={curSide}
                            text={displayed}
                            fullText={dialogueText}
                        />
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body,
    )
}
